import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';

// <-- MUDANÇA: Importa o UserProvider
import { UserProvider } from './contexts/UserContext';
import { auth } from './firebaseConfig';

// ─── Controle de sessão de 24h ───────────────────────────────────────────────
const SESSION_DURATION_MS = 3 * 60 * 60 * 1000; // 3 horas

const clearSession = async () => {
  localStorage.removeItem('sessionStart');

  // Limpa todos os cookies do domínio
  document.cookie.split(';').forEach((cookie) => {
    const name = cookie.split('=')[0].trim();
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  });

  await auth.signOut();
};

const SessionGuard = () => {
  useEffect(() => {
    const checkSession = async () => {
      const sessionStart = localStorage.getItem('sessionStart');
      if (!sessionStart) return;

      const elapsed = Date.now() - parseInt(sessionStart, 10);
      if (elapsed >= SESSION_DURATION_MS) {
        await clearSession();
      }
    };

    // Verifica imediatamente ao carregar
    checkSession();

    // Continua verificando a cada minuto
    const interval = setInterval(checkSession, 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  return null;
};
// ────────────────────────────────────────────────────────────────────────────

// Layouts e Componentes de Autenticação
import LoginLayout from './components/LoginLayout';
import LoginForm from './components/LoginForm';
import SignUp from './components/SignUp';
import ForgotPassword from './components/ForgotPassword';
import PublicOuvidoriaPage from './components/Ouvidoria/PublicOuvidoriaPage';
import PeopleAnalytics from './components/PeopleAnalytics';

// Componente de Proteção de Rota
import ProtectedRoute from './components/ProtectedRoute';

// Páginas Principais (Layout do Dashboard)
import Dashboard from './components/Dashboard';
import MeuPainel from './components/MeuPainel';
import Clients from './components/Clients';
import Faturas from './components/Faturas';
import Cadastro from './components/Cadastro';
import Organograma from './components/Organograma';
import PainelLideranca from './components/PainelLideranca';
import Configuracoes from './components/ConfiguracoesPeople';
import NotificationTemplateEditor from './components/ConfiguracoesNotificacoes';
import OuvidoriaPage from './components/Ouvidoria/OuvidoriaPage';
import MeusResultados from './components/MeusResultados';

// Páginas de Avaliação de Desempenho
import AvaliacaoDesempenho from './components/AvaliacaoDeDesempenho/AvaliacaoDesempenho';
import CriarAvaliacao from './components/AvaliacaoDeDesempenho/CriarAvaliacao';
import ProgressoAvaliacao from './components/AvaliacaoDeDesempenho/ProgressoAvaliacao';
import MinhasTarefasAvaliacao from './components/AvaliacaoDeDesempenho/MinhasTarefasAvaliacao';
import ResponderAvaliacao from './components/AvaliacaoDeDesempenho/ResponderAvaliacao';

import AbsenteismoManager from './components/AbsenteismoManager';
import MapaCalor from './components/MapaCalor';
import MoodManagement from './components/Mood/MoodManagement';
import PublicMoodPage from './components/Mood/PublicMoodPage';

// Páginas de Colaborador do Mês
import ColaboradorDoMes from './components/ColaboradorDoMes/ColaboradorDoMes';
import CriarColaboradorDoMes from './components/ColaboradorDoMes/CriarColaboradorDoMes';
import VotarColaborador from './components/ColaboradorDoMes/VotarColaborador';
import GerenciarColaboradorDoMes from './components/ColaboradorDoMes/GerenciarColaboradorDoMes';

const App = () => (
  <Router>
    {/* <-- MUDANÇA: UserProvider envolve todas as rotas --> */}
    <UserProvider>
      {/* Monitora a expiração de sessão a cada minuto */}
      <SessionGuard />
      <Routes>
        {/* Rotas Públicas */}
        <Route path="/" element={<LoginLayout><LoginForm /></LoginLayout>} />
        <Route path="/signup" element={<LoginLayout><SignUp /></LoginLayout>} />
        <Route path="/forgot-password" element={<LoginLayout><ForgotPassword /></LoginLayout>} />
        <Route path="/ouvidoria-publica" element={<PublicOuvidoriaPage />} />

        {/* --- Rotas Protegidas --- */}
        {/* <-- MUDANÇA: Estrutura simplificada, sem UserProfileWrapper --> */}
        
        {/* Rota principal do Dashboard */}
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard><MeuPainel /></Dashboard></ProtectedRoute>} />
        
        {/* Rotas que NÃO dependem de módulo */}
        <Route path="/clientes" element={<ProtectedRoute><Dashboard><Clients /></Dashboard></ProtectedRoute>} />
        <Route path="/faturas" element={<ProtectedRoute><Dashboard><Faturas /></Dashboard></ProtectedRoute>} />
        <Route path="/cadastro" element={<ProtectedRoute><Dashboard><Cadastro /></Dashboard></ProtectedRoute>} />
        <Route path="/absenteismo" element={<ProtectedRoute><Dashboard><AbsenteismoManager /></Dashboard></ProtectedRoute>} />
        <Route path="/organograma" element={<ProtectedRoute><Dashboard><Organograma /></Dashboard></ProtectedRoute>} />
        <Route path="/minha-equipe" element={<ProtectedRoute><Dashboard><PainelLideranca /></Dashboard></ProtectedRoute>} />
        <Route path="/meus-resultados" element={<ProtectedRoute><Dashboard><MeusResultados /></Dashboard></ProtectedRoute>} />
        <Route path="/configuracoes-people" element={<ProtectedRoute><Dashboard><Configuracoes /></Dashboard></ProtectedRoute>} />
        <Route path="/configuracoes-people/notificacoes" element={<ProtectedRoute><Dashboard><NotificationTemplateEditor /></Dashboard></ProtectedRoute>} />
        <Route path="analise-pessoas" element={<ProtectedRoute><Dashboard><PeopleAnalytics /></Dashboard></ProtectedRoute>} />

        
        {/* --- Rotas Protegidas POR MÓDULO --- */}
        
        {/* <-- MUDANÇA: Adicionada a prop 'requiredModule' --> */}
        <Route path="/ouvidoria" element={<ProtectedRoute requiredModule="OUVIDORIA"><Dashboard><OuvidoriaPage /></Dashboard></ProtectedRoute>} />
        <Route path="/mapa-calor" element={<ProtectedRoute><Dashboard><MapaCalor /></Dashboard></ProtectedRoute>} />
        {/* Rotas do Módulo de Avaliações */}
        <Route path="/avaliacoes" element={<ProtectedRoute requiredModule="AVALIACOES"><Dashboard><AvaliacaoDesempenho /></Dashboard></ProtectedRoute>} />
        <Route path="/avaliacoes/criar" element={<ProtectedRoute requiredModule="AVALIACOES"><Dashboard><CriarAvaliacao /></Dashboard></ProtectedRoute>} />
        <Route path="/avaliacoes/progresso/:cycleId" element={<ProtectedRoute requiredModule="AVALIACOES"><Dashboard><ProgressoAvaliacao /></Dashboard></ProtectedRoute>} />
        <Route path="/avaliacoes/editar/:cycleId" element={<ProtectedRoute requiredModule="AVALIACOES"><Dashboard><CriarAvaliacao /></Dashboard></ProtectedRoute>} />
        <Route path="/avaliacoes/duplicar/:cycleId" element={<ProtectedRoute requiredModule="AVALIACOES"><Dashboard><CriarAvaliacao /></Dashboard></ProtectedRoute>} />
        <Route path="/avaliacoes/minhas-tarefas" element={<ProtectedRoute requiredModule="AVALIACOES"><Dashboard><MinhasTarefasAvaliacao /></Dashboard></ProtectedRoute>} />
        <Route path="/avaliacoes/responder/:responseId" element={<ProtectedRoute requiredModule="AVALIACOES"><Dashboard><ResponderAvaliacao /></Dashboard></ProtectedRoute>} />
                
        {/* Nova Rota de Gestão de Humor */}
        <Route path="/gestao-humor" element={<ProtectedRoute><Dashboard><MoodManagement /></Dashboard></ProtectedRoute>} />


        {/* ROTAS DO COLABORADOR DO MÊS */}
        <Route path="/colaborador-do-mes" element={<ProtectedRoute><Dashboard><ColaboradorDoMes /></Dashboard></ProtectedRoute>} />
        <Route path="/colaborador-do-mes/criar" element={<ProtectedRoute><Dashboard><CriarColaboradorDoMes /></Dashboard></ProtectedRoute>} />
        <Route path="/colaborador-do-mes/votar/:surveyId" element={<ProtectedRoute><Dashboard><VotarColaborador /></Dashboard></ProtectedRoute>} />
        <Route path="/colaborador-do-mes/gerenciar/:surveyId" element={<ProtectedRoute><Dashboard><GerenciarColaboradorDoMes /></Dashboard></ProtectedRoute>} />

        {/* Rota PÚBLICA do Termômetro */}
        <Route path="/pesquisa-humor" element={<PublicMoodPage />} />

        
      </Routes>
    </UserProvider>
  </Router>
);

export default App;